import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Save, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'drive_off', label: 'Drive Off / Debt', help: 'Entered vs incidents minus recoveries', fuelOnly: true },
  { key: 'retail', label: 'Retail Sales', help: 'Entered vs calculated retail fuel sales', fuelOnly: true },
  { key: 'bunkering', label: 'Bunkering Sales', help: 'Entered vs fuel card bunkering total', fuelOnly: true },
  { key: 'z_reading', label: 'Total Sales* (Z-Reading)', help: 'Z-Reading vs retail + bunkering + drive off', fuelOnly: true },
  { key: 'cash', label: 'Cash Variance - Admin Approval', help: 'Above this, admin approval is required' },
  { key: 'cash_reason', label: 'Cash Variance - Reason & Store Email', help: 'At or above this, staff must give a reason and the store is emailed' },
];

export const VarianceLimitsSettings = ({ stores }) => {
  const [storeId, setStoreId] = useState('');
  const [limits, setLimits] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const store = stores.find(s => s.id === storeId);

  useEffect(() => {
    if (!storeId && stores.length) setStoreId(stores[0].id);
  }, [stores, storeId]);

  useEffect(() => {
    if (!storeId) return;
    setLimits(null);
    axios.get(`${API}/stores/${storeId}/variance-limits`)
      .then(res => { setLimits(res.data.limits); setDefaults(res.data.defaults); })
      .catch(() => toast.error('Failed to load variance limits'));
  }, [storeId]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/stores/${storeId}/variance-limits`, limits);
      setLimits(res.data.limits);
      toast.success('Variance limits saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save variance limits');
    } finally {
      setSaving(false);
    }
  };

  const fields = FIELDS.filter(f => !f.fuelOnly || store?.store_type === 'fuel_station');

  return (
    <Card data-testid="variance-limits-settings">
      <CardHeader>
        <CardTitle>Variance Limits</CardTitle>
        <CardDescription>Set the allowed ± variance for each check per store. Variances over a limit need Admin Approval.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="max-w-xs space-y-1.5">
          <Label>Store</Label>
          <Select value={storeId} onValueChange={setStoreId}>
            <SelectTrigger data-testid="variance-limits-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
            <SelectContent>
              {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!limits ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              {fields.map(f => (
                <div key={f.key} className="space-y-1.5 p-3 rounded-lg border">
                  <Label htmlFor={`limit-${f.key}`}>{f.label}</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">± £</span>
                    <Input
                      id={`limit-${f.key}`}
                      type="number" step="0.01" min="0"
                      value={limits[f.key]}
                      onChange={e => setLimits(prev => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-32 font-mono"
                      data-testid={`variance-limit-${f.key}`}
                    />
                    <span className="text-xs text-muted-foreground">Default £{Number(defaults[f.key] ?? 0).toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{f.help}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} data-testid="save-variance-limits-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Limits
              </Button>
              <Button variant="outline" onClick={() => setLimits(prev => ({ ...prev, ...defaults }))} data-testid="reset-variance-limits-btn">
                <RotateCcw className="h-4 w-4 mr-2" />Reset to Defaults
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
